// @ts-check
import { ArrowDown, ArrowUp, Eye, EyeOff } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminSiteSettingsService from '../services/adminSiteSettingsService';

/**
 * Generic reorder + show/hide manager for a page's list of sections. One component for
 * both concerns since they're really one feature (control over a page's section list) —
 * reusable as-is for any page that needs this (homepage today; PDP/collection pages
 * later): pass that page's section list, its order setting key, and (optionally) a
 * section that must always render first and can't be reordered.
 *
 * @param {{
 *   sections: { key: string, label: string }[],
 *   orderSettingKey: string,
 *   enabledPrefix?: string,
 *   enabledSuffix?: string,
 *   pinnedFirstKey?: string,
 * }} props
 */
const SectionManager = ({
  sections,
  orderSettingKey,
  enabledPrefix = 'section_',
  enabledSuffix = '_enabled',
  pinnedFirstKey,
}) => {
  const toast = useToast();
  const [order, setOrder] = useState(/** @type {string[]} */ (sections.map((s) => s.key)));
  const [enabled, setEnabled] = useState(/** @type {Record<string, boolean>} */ ({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const enabledKeyFor = useCallback((key) => `${enabledPrefix}${key}${enabledSuffix}`, [enabledPrefix, enabledSuffix]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const allSettings = await adminSiteSettingsService.getAllSettings();
      const valueByKey = Object.fromEntries(allSettings.map((s) => [s.key, s.value]));

      const enabledState = /** @type {Record<string, boolean>} */ ({});
      sections.forEach((s) => {
        // Missing/unconfigured defaults to shown — matches the customer frontend's own fallback.
        enabledState[s.key] = valueByKey[enabledKeyFor(s.key)] !== 'false';
      });
      setEnabled(enabledState);

      const knownKeys = sections.map((s) => s.key);
      const stored = (valueByKey[orderSettingKey] || '')
        .split(',')
        .map((k) => k.trim())
        .filter((k) => knownKeys.includes(k) && k !== pinnedFirstKey);
      const missing = knownKeys.filter((k) => k !== pinnedFirstKey && !stored.includes(k));
      const rest = [...stored, ...missing];
      setOrder(pinnedFirstKey ? [pinnedFirstKey, ...rest] : rest);
    } catch (err) {
      toast.error(err.message || 'Failed to load sections');
    } finally {
      setLoading(false);
    }
  }, [sections, orderSettingKey, pinnedFirstKey, enabledKeyFor, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= order.length) return;
    if (order[index] === pinnedFirstKey || order[target] === pinnedFirstKey) return;
    const updated = [...order];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setOrder(updated);
  };

  const toggle = (key) => {
    setEnabled((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = { [orderSettingKey]: order.filter((k) => k !== pinnedFirstKey).join(',') };
      sections.forEach((s) => {
        updates[enabledKeyFor(s.key)] = enabled[s.key] ? 'true' : 'false';
      });
      await adminSiteSettingsService.bulkUpdate(updates);
      toast.success('Sections saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save sections');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-muted-foreground text-sm">Loading sections...</div>;
  }

  const labelFor = (key) => sections.find((s) => s.key === key)?.label || key;

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        {order.map((key, index) => {
          const isPinned = key === pinnedFirstKey;
          const isEnabled = enabled[key] !== false;
          return (
            <div
              key={key}
              className={`flex items-center justify-between p-3 rounded-lg border transition ${
                isEnabled ? 'border-border bg-card' : 'border-border/60 bg-muted/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-muted-foreground/70 w-5 text-center font-mono text-xs">{index + 1}</span>
                {isEnabled ? (
                  <Eye size={16} className="text-green-600 dark:text-green-400" />
                ) : (
                  <EyeOff size={16} className="text-muted-foreground/70" />
                )}
                <span className={`text-sm ${isEnabled ? 'text-foreground' : 'text-muted-foreground/70'}`}>
                  {labelFor(key)}
                </span>
                {isPinned && <span className="text-xs text-muted-foreground/70">(always first)</span>}
              </div>
              <div className="flex items-center gap-3">
                {!isPinned && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={index === 0 || order[index - 1] === pinnedFirstKey}
                      className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                      title="Move up"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={index === order.length - 1}
                      className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                      title="Move down"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                      isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end pt-1">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50 rounded-lg text-sm"
        >
          {saving ? 'Saving...' : 'Save Sections'}
        </button>
      </div>
    </div>
  );
};

export default SectionManager;
